import { html, LitElement, nothing, TemplateResult } from 'lit';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../types/common/types';
import { translateFn } from '../../../../../utils/translator.helper';
import {
  getIconNameItemType,
  RiskManagementSideMenuEvents,
  renderCategories,
  renderErrorUserDetailsState,
  renderEmptyUserDetailsState,
  setFilterOptionsForUserDropdown,
  getFindingsDetailsItemType,
} from '../../../common/common';
import { customElement, property, query } from 'lit/decorators.js';
import { InitialDropdownDetails } from '../types/types';
import { GzSingleSelectComplexWebComponent } from '../../../../../ui/gzSingleSelectComplex';
import { styles } from '../view/styles';
import { API_TYPE_USER_FILTER_RISK_MANAGEMENT } from '../../../../../webcomponents.constants';

/**
 * @summary Type here the description of this component
 * @tag device-details
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('user-details')
export class UserDetailsWebComponent extends LitElement {
  /**
   * See https://lit-element.polymer-project.org/guide/properties
   * Write here the description of this field
   */
  @property({ type: String, attribute: true, reflect: true })
  detailsTitle = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  showUserDropdown = false;

  @property({ type: Object, attribute: false })
  initialDropdownDetails = null as null | InitialDropdownDetails;

  @property({ type: Object, attribute: false })
  userInformation = null as null | any;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasErrors = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  emptyState = false;

  @property({ type: String, attribute: false })
  extraDetailsPanelTitle = null as string | null;

  @property({ type: String, attribute: false })
  companyId = null as string | null;

  @query('#user-details-selection')
  public readonly userDetailsDropdown!: GzSingleSelectComplexWebComponent;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  updated(changedProperties): void {
    super.updated(changedProperties);

    if (changedProperties.has('initialDropdownDetails')) {
      this.updateComplete.then(() => {
        setFilterOptionsForUserDropdown(this);
      });
    }
  }

  render() {
    if (this.emptyState) {
      return nothing;
    }

    return html` ${this.renderTitle()} ${this.renderUserComponents()}`;
  }

  private renderTitle() {
    if (!this.detailsTitle) {
      return nothing;
    }

    return html`<gz-title
      fontSize="${FontSizes.PARAGRAPH_11}"
      fontWeight="${FontWeights.SEMIBOLD}"
      class="section-title text-uppercase mb-2"
      >${this.detailsTitle}
    </gz-title>`;
  }

  private renderUserComponents() {
    if (this.hasErrors) {
      return renderErrorUserDetailsState();
    }

    if (this.showUserDropdown && !this.initialDropdownDetails) {
      return html`<gz-loader></gz-loader>`;
    }

    return html` ${this.renderUserDropdown()} ${this.renderUserDetails()} `;
  }

  private renderUserDropdown() {
    if (!this.showUserDropdown) {
      return nothing;
    }

    return html` <gz-single-select-complex
      id="user-details-selection"
      class="pb-1 w-full user-details-selection"
      .apiType=${API_TYPE_USER_FILTER_RISK_MANAGEMENT}
      .label=${'Identity name'}
      .labelWidth=${LabelWidthValues.FULL}
      .noSearchResultsString=${translateFn('No results')}
      .confirmActionButtonString=${translateFn('Apply')}
      .searchPlaceholderString=${translateFn('Search here')}
      .errorMessage=${translateFn('Error')}
      @selectionChange=${this.handleDropdownValueChanged}
      isFilter
      cannotDelete="true"
    ></gz-single-select-complex>`;
  }

  private renderUserDetails() {
    if (!this.userInformation) {
      return html` <gz-loader></gz-loader>`;
    }

    if (Object.keys(this.userInformation).length === 0) {
      return renderEmptyUserDetailsState(this.showUserDropdown);
    }

    const finalTemplate: any = [];

    for (const [category, information] of Object.entries(this.userInformation)) {
      const categoryNumber = parseInt(category, 10);
      if (!Array.isArray(information)) {
        continue;
      }

      const categoryTitle: string = getFindingsDetailsItemType(categoryNumber);
      const categoryItemsNumber: string = '(' + information.length + ')';
      const categoryIconClass: string = getIconNameItemType(categoryNumber);

      const userDetailsCategoryInformation: TemplateResult = html`
        ${renderCategories(categoryIconClass, categoryTitle, categoryItemsNumber, async () => {
          this.handleUserExtraDetailsButtonClick(information, categoryNumber);
        })}
      `;

      finalTemplate.push(userDetailsCategoryInformation);
    }

    return finalTemplate;
  }

  private handleUserExtraDetailsButtonClick(items, category) {
    const eventDetail = {
      showExtraDetailsPanel: true,
      items,
      itemsType: category,
      companyId: this.initialDropdownDetails?.companyId ?? this.companyId,
    };

    if (this.showUserDropdown) {
      const currentSelectedUserFromDropdown = this.userDetailsDropdown.getSelectedOption();

      eventDetail['dropdownDetails'] = {
        userName: currentSelectedUserFromDropdown?.label,
        userId: currentSelectedUserFromDropdown?.value,
        companyId: this.initialDropdownDetails?.companyId,
        userIds: this.initialDropdownDetails?.userIds,
        indicatorId: this.initialDropdownDetails?.indicatorId,
      };
    }

    if (this.extraDetailsPanelTitle) {
      eventDetail['extraDetailsPanelTitle'] = this.extraDetailsPanelTitle;
    }

    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.USER_DETAILS_SHOW_EXTRA_DATA, {
        detail: eventDetail,
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleDropdownValueChanged(event) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.USER_DETAILS_CHANGED, {
        detail: {
          userId: event.detail.selectedValue,
          companyId: this.initialDropdownDetails?.companyId,
          indicatorId: this.initialDropdownDetails?.indicatorId,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-details': UserDetailsWebComponent;
  }
}
